
import { mapGetters, mapActions } from 'vuex'
import NavigationMenuItems from 'assets/data/navigation'
import AppLabel from '../AppLabel'
import LocaleChanger from '../default/LocaleChanger'
import ProfileDropDown from '../default/ProfileDropDown'
import AppStatusBadge from '../AppStatusBadge'
import permissions from '../../assets/js/enums/ModeratorPermissions'
import TheSearch from './TheSearch.vue'
import AppSidebarCreditInfo from '~/components/molecules/AppSidebarCreditInfo'

export default {
  name: 'DashboardSidebarDesktop',
  components: { AppStatusBadge, AppLabel, ProfileDropDown, LocaleChanger, TheSearch, AppSidebarCreditInfo },
  data () {
    return {
      showSearch: false,
      faqSections: [],
      vueTippyOptions: {
        placement: 'right',
        arrow: true,
        popperOptions: {
          modifiers: {
            preventOverflow: {
              enabled: false
            },
            hide: {
              enabled: false
            }
          }
        }
      }
    }
  },
  async fetch () {
    this.faqSections = await this.$axios.get('v1/faq-sections').then(res => res.data)

    if (!this.$auth.user.acting_user) {
      this.fetchAlertSets(this.$store.getters['user/currentId'])
    } else {
      this.fetchAlertSets(this.$auth.user.acting_user.id)
    }
  },
  computed: {
    ...mapGetters({
      isSidebarOpen: 'sidebar/isOpen',
      activeSidebar: 'sidebar/activeSidebar',
      alertSets: 'sidebar/alertSets',
      expandNavigation: 'sidebar/isExpandedNavigation'
    }),
    menuItems () {
      const menuAlertSets = []

      if (this.alertSets) {
        for (const alertSet of this.alertSets) {
          menuAlertSets.push({
            title: alertSet.title,
            noTranslation: true,
            active: alertSet.active,
            permission: permissions.ALERTS,
            route: {
              name: 'dashboard-alerts-alert-sets-id',
              params: {
                id: alertSet.id
              }
            }
          })
        }

        for (const items of NavigationMenuItems) {
          if (items.title === 'dashboard.nav.alerts.title') {
            if (items.sections[0].title === 'dashboard.nav.alerts.alert_set') {
              items.sections[0].items = menuAlertSets
            }
          }
        }

        this.$store.commit('sidebar/setActiveSidebar', 'navigation')
      }

      return NavigationMenuItems.filter((item) => {
        return typeof item.hasPermission === 'undefined' || item.hasPermission(this.$auth.user)
      })
    },
    currentSubNav () {
      return this.menuItems.find((item) => {
        if (!item.sections) {
          return false
        }

        return item.sections.find((section) => {
          return section.items.find((item) => {
            return this.$route.name.includes(item.route.name)
          })
        })
      })
    },
    visibleSubNavSections () {
      if (!this.currentSubNav) {
        return []
      }

      if (!this.$auth.user.acting_user) {
        return this.currentSubNav.sections
      }

      const visibleSections = []

      this.currentSubNav.sections.forEach((section) => {
        const tempItems = []
        section.items.forEach((item) => {
          if (item.permission && item.permission & this.$auth.user.acting_user.permission) {
            tempItems.push(item)
          }
        })

        if (tempItems.length > 0) {
          visibleSections.push({
            title: section.title,
            items: tempItems
          })
        }
      })

      return visibleSections
    },
    showSidebarToggle () {
      return !this.isSidebarOpen && this.activeSidebar
    }
  },

  mounted () {
    if (this.$route.name.includes('dashboard-faqs-id')) {
      this.$store.commit('sidebar/setActiveSidebar', 'faq')
    }

    this.$nextTick(() => {
      if (this.currentSubNav && this.currentSubNav.sections) {
        this.$store.commit('sidebar/setActiveSidebar', 'navigation')
      }
    })

    if (localStorage.getItem('expandNavigation') === 'false') {
      this.$store.commit('sidebar/setExpandedNavigation', false)
    }

    if (!this.$auth.user.acting_user) {
      this.fetchAlertSets(this.$store.getters['user/currentId'])
    } else {
      this.fetchAlertSets(this.$auth.user.acting_user.id)
    }
  },

  methods: {
    toggleNavigation (open) {
      this.$store.commit('sidebar/setExpandedNavigation', open)
      localStorage.setItem('expandNavigation', open)

      if (open) {
        this.$store.commit('sidebar/setIsOpen', true)
      }
    },
    ...mapActions({
      fetchAlertSets: 'sidebar/fetchAlertSets'
    }),
    getClassForItem (item) {
      if (this.isSectionActiveforRootItem(item)) {
        return 'bg-yellow-300 text-gray-redesign-900'
      }

      return 'hover:bg-gray-redesign-700'
    },
    openSubNav (item) {
      if (!this.currentSubNav || (this.currentSubNav && item.title !== this.currentSubNav.title)) {
        if (!this.$auth.user.acting_user) {
          this.$router.push(item.sections[0].items[0].route)
        } else {
          const items = []
          item.sections.forEach(section => items.push(...section.items))

          const firstAvailableItem = items.find((item) => {
            return item.permission && item.permission & this.$auth.user.acting_user.permission
          })

          this.$router.push(firstAvailableItem.route)
        }
      }

      if (!this.isSidebarOpen) {
        this.$store.commit('sidebar/setIsOpen', true)
      }

      if (this.activeSidebar !== 'navigation') {
        this.openSpecialSubNav('navigation')
      }
    },
    openSpecialSubNav (name) {
      if (this.activeSidebar) {
        this.resetSubNav()

        setTimeout(() => {
          this.$store.commit('sidebar/setActiveSidebar', name)
        }, 500)
      } else {
        this.$store.commit('sidebar/setActiveSidebar', name)
      }
    },
    resetSubNav () {
      this.closeSubNav()
      this.$store.commit('sidebar/setActiveSidebar', null)
    },
    closeSubNav () {
      this.$store.commit('sidebar/setIsOpen', false)
    },
    isSectionActiveforRootItem (item) {
      const result = item.sections.find((section) => {
        return section.items.find((item) => {
          return this.$route.name.includes(item.route.name)
        })
      })

      return !!result
    },
    openSidebar () {
      this.$store.commit('sidebar/setIsOpen', true)
    },
    userHasPermissionForMenuItem (item) {
      if (!this.$auth.user.acting_user) {
        return true
      }

      return item.permission & this.$auth.user.acting_user.permission
    },
    userHasPermissionForSubMenuItem (item) {
      if (!this.$auth.user.acting_user) {
        return true
      }

      let hasPermission = false

      const permission = this.$auth.user.acting_user.permission

      if (item.permission & permission) {
        hasPermission = true
      }

      item.sections.forEach((section) => {
        if (section.permission && section.permission & permission) {
          hasPermission = true
        }

        section.items.forEach((item) => {
          if (item.permission && item.permission & permission) {
            hasPermission = true
          }
        })
      })

      return hasPermission
    },
    showItemToUser (item) {
      return !(this.$auth.user.acting_user && item.noActingUser)
    }
  }
}
